import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calculator, Clock, Ruler, BookOpen, PenTool, Users, FileSpreadsheet, UserPlus, LineChart, Target, Bell } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
import { dataLayer } from '../../../data/dataLayer';
import { getRoleThemeClasses } from '../../logic/uiHelpers';
// Premium vak-tiles voor de learner-keuze. Gradient bg + emoji-accent + recommended-hint.
// Volgorde: makkelijkste/meest universele eerst; groep-7+ vakken aan het einde via `minGroup`.
const SUBJECT_TILES: Array<{
  id: string;
  label: string;
  emoji: string;
  icon: typeof Calculator;
  bg: string;
  border: string;
  iconColor: string;
  labelColor: string;
  minGroup?: number;
  recommended?: boolean;
}> = [{
  id: 'REK',
  label: 'Rekenen',
  emoji: '🧮',
  icon: Calculator,
  bg: 'bg-gradient-to-br from-orange-50 to-amber-100',
  border: 'border-orange-200',
  iconColor: 'text-orange-500',
  labelColor: 'text-orange-900',
  recommended: true
}, {
  id: 'GET',
  label: 'Getallen',
  emoji: '🔢',
  icon: Target,
  bg: 'bg-gradient-to-br from-blue-50 to-sky-100',
  border: 'border-blue-200',
  iconColor: 'text-blue-500',
  labelColor: 'text-blue-900'
}, {
  id: 'MET',
  label: 'Meten',
  emoji: '📏',
  icon: Ruler,
  bg: 'bg-gradient-to-br from-emerald-50 to-teal-100',
  border: 'border-emerald-200',
  iconColor: 'text-emerald-500',
  labelColor: 'text-emerald-900'
}, {
  id: 'SPR',
  label: 'Spelling',
  emoji: '✏️',
  icon: PenTool,
  bg: 'bg-gradient-to-br from-purple-50 to-fuchsia-100',
  border: 'border-purple-200',
  iconColor: 'text-purple-500',
  labelColor: 'text-purple-900'
}, {
  id: 'TAA',
  label: 'Taal',
  emoji: '📖',
  icon: BookOpen,
  bg: 'bg-gradient-to-br from-rose-50 to-pink-100',
  border: 'border-rose-200',
  iconColor: 'text-rose-500',
  labelColor: 'text-rose-900',
  minGroup: 7
}, {
  id: 'TIJD',
  label: 'Tijd',
  emoji: '⏰',
  icon: Clock,
  bg: 'bg-gradient-to-br from-cyan-50 to-sky-100',
  border: 'border-cyan-200',
  iconColor: 'text-cyan-500',
  labelColor: 'text-cyan-900',
  minGroup: 7
}];
interface OnboardingFollowUpStepProps {
  role: AccountRole;
  groupLevel?: number;
  onComplete: () => void;
  /**
   * @deprecated for the learner role — onboarding no longer navigates
   * directly to a module (caused a dead-end and skipped the home page).
   * Kept in the signature for non-learner roles (teacher).
   */
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
}
export function OnboardingFollowUpStep({
  role,
  groupLevel = 6,
  onComplete,
  onNavigateToMyClass
}: OnboardingFollowUpStepProps) {
  const theme = getRoleThemeClasses(role);
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'celebrating' as const,
          message: 'Klaar voor de start! Welk vak wil je als eerste oefenen?'
        };
      case 'parent':
        return {
          emotion: 'celebrating' as const,
          message: 'Gelukt! Laten we even kijken wat u allemaal kunt doen op het dashboard.'
        };
      case 'teacher':
        return {
          emotion: 'excited' as const,
          message: 'Klas aangemaakt! Hoe wilt u uw leerlingen toevoegen?'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Klaar!'
        };
    }
  };
  const config = getRoleConfig();
  // R-AUTH-2 (mei 2026): the learner subject pick used to call
  // `onNavigateToModule` followed by `onComplete()`, which landed the
  // learner in an undefined state and skipped the home page entirely.
  //
  // New flow:
  //  1. Record the chosen subject as `firstFocusSubject` in learner prefs.
  //  2. Complete onboarding normally → AuthGuard routes to home.
  //  3. The home page reads `firstFocusSubject` once and lets FloatingDjay
  //     point the learner at that subject (handover, not navigation jump).
  const handleModuleClick = async (moduleId: string) => {
    try {
      const prefs = await dataLayer.getLearnerPreferences();
      await dataLayer.saveLearnerPreferences({
        ...prefs,
        firstFocusSubject: moduleId
      });
    } catch {
      // Non-fatal — onboarding still completes, learner just lands
      // on home without the contextual pointer.
    }
    onComplete();
  };
  const handleTeacherAction = () => {
    if (onNavigateToMyClass) {
      onNavigateToMyClass();
    }
    onComplete();
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-105">
      <div className="flex flex-col items-center mb-6 relative" data-id="element-106">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-107">
          <DjayAvatar emotion={config.emotion} size="lg" data-id="element-108" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-109">
          <DjayBubble message={config.message} position="top" colorScheme={theme.colorScheme} autoDismissMs={0} data-id="element-110" />
        </div>
      </div>

      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border ${theme.border} relative overflow-hidden`} data-id="element-111">
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${theme.gradient}`} data-id="element-112" />
        <OnboardingProgress currentStep={1} role={role} data-id="element-113" />

        {role === 'learner' && <div className="space-y-4" data-id="element-114">
            <h2 className="text-xl font-bold text-center text-gray-900 mb-1" data-id="element-115">
              Kies een vak
            </h2>
            <p className="text-center text-sm text-slate-500 mb-5" data-id="element-116">
              Je kunt later altijd switchen.
            </p>
            <div className="grid grid-cols-2 gap-3" data-id="element-117">
              {SUBJECT_TILES.filter(t => !t.minGroup || groupLevel >= t.minGroup).map((tile, idx) => {
            const Icon = tile.icon;
            return <motion.button key={tile.id} onClick={() => handleModuleClick(tile.id)} className={`relative flex flex-col items-center justify-center p-4 ${tile.bg} rounded-2xl border ${tile.border} text-left overflow-hidden group focus:outline-none focus:ring-2 focus:ring-orange-300`} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.05 + idx * 0.04,
              duration: 0.3
            }} whileHover={{
              scale: 1.03,
              y: -2
            }} whileTap={{
              scale: 0.96
            }} aria-label={`Kies ${tile.label}`} data-id="element-118">
                    {/* Top-right emoji accent */}
                    <span className="absolute top-2 right-2 text-lg opacity-60 group-hover:opacity-100 transition-opacity" aria-hidden="true" data-id="element-119">
                      {tile.emoji}
                    </span>
                    {/* Subtle shine on hover */}
                    <span className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true" data-id="element-120" />
                    <Icon className={`w-8 h-8 ${tile.iconColor} mb-2`} data-id="element-121" />
                    <span className={`font-semibold ${tile.labelColor}`} data-id="element-122">
                      {tile.label}
                    </span>
                    {tile.recommended && <span className="mt-1 text-[10px] font-bold uppercase tracking-wide text-orange-600" data-id="element-123">
                        👆 begin hier
                      </span>}
                  </motion.button>;
          })}
            </div>
            <button onClick={onComplete} className="w-full mt-4 py-3 text-sm font-semibold text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-2xl transition-colors" data-id="element-124">
              Ik kies later
            </button>
            <p className="text-center text-xs text-slate-400" data-id="element-125">
              Je kunt dit altijd nog doen via het menu.
            </p>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-126">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-127">
              Jouw Dashboard
            </h2>
            <div className="space-y-4" data-id="element-128">
              <div className="flex gap-4 items-start p-4 bg-slate-50 rounded-2xl" data-id="element-129">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-130">
                  <LineChart className="w-5 h-5 text-emerald-600" data-id="element-131" />
                </div>
                <div data-id="element-132">
                  <h3 className="font-semibold text-slate-900" data-id="element-133">
                    Voortgang & Groei
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-134">
                    Bekijk precies waar je kind sterk in is en wat nog extra
                    aandacht nodig heeft.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start p-4 bg-slate-50 rounded-2xl" data-id="element-135">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-136">
                  <Target className="w-5 h-5 text-emerald-600" data-id="element-137" />
                </div>
                <div data-id="element-138">
                  <h3 className="font-semibold text-slate-900" data-id="element-139">
                    Gerichte Tips
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-140">
                    Krijg wekelijks tips over hoe je je kind het beste kunt
                    helpen bij lastige onderwerpen.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start p-4 bg-slate-50 rounded-2xl" data-id="element-141">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-142">
                  <Bell className="w-5 h-5 text-emerald-600" data-id="element-143" />
                </div>
                <div data-id="element-144">
                  <h3 className="font-semibold text-slate-900" data-id="element-145">Notificaties</h3>
                  <p className="text-sm text-slate-500" data-id="element-146">
                    Stel in wanneer je updates wilt ontvangen over de
                    oefensessies.
                  </p>
                </div>
              </div>
            </div>
            <Button onClick={onComplete} className="w-full py-6 text-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-147">
              Naar mijn dashboard
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-148" />
            </Button>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-149">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-150">
              Leerlingen toevoegen
            </h2>
            <div className="space-y-3" data-id="element-151">
              <button onClick={handleTeacherAction} className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-colors border border-indigo-100 text-left" data-id="element-152">
                <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0" data-id="element-153">
                  <Users className="w-5 h-5 text-indigo-700" data-id="element-154" />
                </div>
                <div data-id="element-155">
                  <h3 className="font-semibold text-indigo-900" data-id="element-156">
                    Koppelcode delen
                  </h3>
                  <p className="text-sm text-indigo-700/70" data-id="element-157">
                    Laat leerlingen zelf lid worden met een code
                  </p>
                </div>
              </button>
              <button onClick={handleTeacherAction} className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-colors border border-indigo-100 text-left" data-id="element-158">
                <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0" data-id="element-159">
                  <UserPlus className="w-5 h-5 text-indigo-700" data-id="element-160" />
                </div>
                <div data-id="element-161">
                  <h3 className="font-semibold text-indigo-900" data-id="element-162">
                    Handmatig toevoegen
                  </h3>
                  <p className="text-sm text-indigo-700/70" data-id="element-163">
                    Maak zelf accounts aan voor leerlingen
                  </p>
                </div>
              </button>
              <button onClick={handleTeacherAction} className="w-full flex items-center gap-4 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-colors border border-indigo-100 text-left" data-id="element-164">
                <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center flex-shrink-0" data-id="element-165">
                  <FileSpreadsheet className="w-5 h-5 text-indigo-700" data-id="element-166" />
                </div>
                <div data-id="element-167">
                  <h3 className="font-semibold text-indigo-900" data-id="element-168">CSV Import</h3>
                  <p className="text-sm text-indigo-700/70" data-id="element-169">
                    Importeer een hele klas tegelijk
                  </p>
                </div>
              </button>
            </div>
            <Button onClick={onComplete} variant="secondary" className="w-full" data-id="element-170">
              Sla over voor nu
            </Button>
            <p className="text-center text-sm text-gray-500 mt-2" data-id="element-171">
              Je kunt later leerlingen toevoegen via 'Mijn Klas'.
            </p>
          </div>}
      </div>
    </motion.div>;
}